import { useState, useEffect, useCallback, useRef } from "react";
import { useUser } from "../../../hooks/useUser";
import styles from "./Finances.module.css";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  Switch,
  Tooltip,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Popover,
  Button,
  Menu,
  MenuItem,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CancelIcon from "@mui/icons-material/Cancel";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CalendarMonth from "@mui/icons-material/CalendarMonth";
import CalendarToday from "@mui/icons-material/CalendarToday";
import EventAvailable from "@mui/icons-material/EventAvailable";
import UnfoldMore from "@mui/icons-material/UnfoldMore";
import UnfoldLess from "@mui/icons-material/UnfoldLess";
import FilterListIcon from "@mui/icons-material/FilterList";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import { TransactionModal } from "../../TransactionModal/TransactionModal";
import { ResgateModal } from "../../ResgateModal/ResgateModal";
import CurrencyInput from "react-currency-input-field";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import {
  getTransactionsByDate,
  UserTransaction,
  getUserTransaction,
  deleteTransaction,
  updateTscCheckTransaction,
  getTransactionsBalance,
  updateValueTransaction,
  closeMonthBalance,
} from "../../../services/api";
import { useSnackbar } from "notistack";
import { UserListAvatar } from "../../UserListAvatar";
import { TransactionList } from "../../TransactionList/TransactionList";
import { YearBalanceList } from "../../YearBalanceList/YearBalanceList";

interface Transaction {
  tscId: number;
  tscName: string;
  tccId: number;
  tccName: string;
  tscValue: number;
  tctId: 1 | 2;
  tctName: "Saída" | "Entrada";
  tscMonth: number;
  tscYear: number;
  tscRecurrence: number;
  tscRecurrenceDate: string;
  tscDateCreated: string;
  tscVersion: number;
  tscIdInitVersion: number;
  usrId: number;
  usrIdTransaction: number;
  usrName: string;
  tscCheck: number;
}

interface TransactionsYear {
  tscMonth: number;
  tscYear: number;
  totalIn: number;
  totalOut: number;
  balance: number;
  accumulatedBalance: number;
  projectedAccumulatedBalance: number;
}

interface EditableValueCellProps {
  initialValue: number;
  onSave: (newValue: number) => Promise<void>;
}

const EditableValueCell = ({
  initialValue,
  onSave,
}: EditableValueCellProps) => {
  const [isEditing, setIsEditing] = useState(false);
  // Usamos um estado local para o valor durante a edição
  const [currentValue, setCurrentValue] = useState(initialValue);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const handleSave = async () => {
    setIsEditing(false);
    // Só chama a API se o valor realmente mudou
    if (currentValue !== initialValue) {
      await onSave(currentValue);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") handleSave();
    if (event.key === "Escape") setIsEditing(false);
  };

  if (isEditing) {
    return (
      <TableCell align="right" onBlur={handleSave} style={{ padding: "0 8px" }}>
        <CurrencyInput
          ref={inputRef}
          name="inline-edit-value"
          defaultValue={currentValue}
          onValueChange={(val, name, values) =>
            setCurrentValue(values?.float ?? 0)
          }
          intlConfig={{ locale: "pt-BR", currency: "BRL" }}
          customInput={TextField}
          onKeyDown={handleKeyDown}
          // size="small"
          // variant="standard"
        />
      </TableCell>
    );
  }

  return (
    <TableCell
      align="right"
      onClick={() => setIsEditing(true)}
      style={{ cursor: "pointer" }}
    >
      <Tooltip title="Clique para editar">
        <Typography>
          {initialValue.toLocaleString("pt-BR", {
            style: "currency",
            currency: "BRL",
          })}
        </Typography>
      </Tooltip>
    </TableCell>
  );
};

const TableFinance = ({
  titulo,
  dados,
  isMonthClosed,
  onAddClick,
  onDeleteClick,
  onUpdateTscCheckClick,
  onUpdateValueTransactionClick,
  onDeleteConfirmClick,
  onCheckConfirmClick,
  onResgateClick,
  resetKey,
}: {
  titulo: string;
  dados: Transaction[];
  isMonthClosed: boolean;
  onAddClick: () => void;
  onDeleteClick: (id: number) => void;
  onUpdateTscCheckClick: (id: number, checked: number) => void;
  onUpdateValueTransactionClick: (id: number, value: number) => void;
  onDeleteConfirmClick: (event: React.MouseEvent<HTMLButtonElement>, id: number) => void;
  onCheckConfirmClick: (event: React.MouseEvent<HTMLElement>, id: number, checked: number) => void;
  onResgateClick?: () => void;
  resetKey?: string;
}) => {
  // Hook para detectar mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [filtro, setFiltro] = useState("");
  const [heightTable, setHeightTable] = useState("300px");
  const [categoriasSelecionadas, setCategoriasSelecionadas] = useState<
    string[]
  >([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [pagamentosSelecionados, setPagamentosSelecionados] = useState<
    number[]
  >([0, 1]);
  const [anchorElPagamento, setAnchorElPagamento] =
    useState<HTMLButtonElement | null>(null);
  const [descricoesSelecionadas, setDescricoesSelecionadas] = useState<
    string[]
  >([]);
  const [anchorElDescricao, setAnchorElDescricao] =
    useState<HTMLButtonElement | null>(null);

  // Reset dos filtros quando o resetKey muda (mudança de mês)
  useEffect(() => {
    if (resetKey) {
      setFiltro("");
      setCategoriasSelecionadas([]);
      setPagamentosSelecionados([0, 1]);
      setDescricoesSelecionadas([]);
      setAnchorEl(null);
      setAnchorElPagamento(null);
      setAnchorElDescricao(null);
    }
  }, [resetKey]);

  // Extrair categorias únicas dos dados
  const categoriasUnicas = Array.from(
    new Set(dados.map((transacao) => transacao.tccName))
  ).sort();

  // Extrair descrições únicas dos dados
  const descricoesUnicas = Array.from(
    new Set(dados.map((transacao) => transacao.tscName))
  ).sort();

  // Não inicializar filtros automaticamente - deixar vazios significa "mostrar tudo"

  const dadosFiltrados = dados.filter((transacao) => {
    const passaFiltroTexto =
      transacao.tscName.toLowerCase().includes(filtro.toLowerCase()) ||
      transacao.tccName.toLowerCase().includes(filtro.toLowerCase()) ||
      transacao.usrName.toLowerCase().includes(filtro.toLowerCase());

    const passaFiltroCategoria =
      categoriasSelecionadas.length === 0 ||
      categoriasSelecionadas.includes(transacao.tccName);

    const passaFiltroDescricao =
      descricoesSelecionadas.length === 0 ||
      descricoesSelecionadas.includes(transacao.tscName);

    const passaFiltroPagamento =
      pagamentosSelecionados.length === 0 ||
      pagamentosSelecionados.includes(transacao.tscCheck);

    return (
      passaFiltroTexto &&
      passaFiltroCategoria &&
      passaFiltroDescricao &&
      passaFiltroPagamento
    );
  });

  const total = dadosFiltrados?.reduce((soma, item) => soma + item.tscValue, 0);
  const totalPagos = dadosFiltrados?.filter(
    (item) => item.tscCheck === 1
  ).length;
  const totalNaoPagos = dadosFiltrados?.filter(
    (item) => item.tscCheck === 0
  ).length;
  const totalLinhas = dadosFiltrados?.length;

  const totalFormatado = total?.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const handleCategoriaChange = (categoria: string) => {
    setCategoriasSelecionadas((prev) => {
      if (prev.includes(categoria)) {
        return prev.filter((cat) => cat !== categoria);
      } else {
        return [...prev, categoria];
      }
    });
  };

  const handleSelecionarTodas = () => {
    setCategoriasSelecionadas(categoriasUnicas);
  };

  const handleOpenFilter = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseFilter = () => {
    setAnchorEl(null);
  };

  const handlePagamentoChange = (valor: number) => {
    setPagamentosSelecionados((prev) => {
      if (prev.includes(valor)) {
        return prev.filter((val) => val !== valor);
      } else {
        return [...prev, valor];
      }
    });
  };

  const handleSelecionarTodosPagamentos = () => {
    setPagamentosSelecionados([0, 1]);
  };

  const handleDeselecionarTodosPagamentos = () => {
    setPagamentosSelecionados([]);
  };

  const handleOpenFilterPagamento = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorElPagamento(event.currentTarget);
  };

  const handleCloseFilterPagamento = () => {
    setAnchorElPagamento(null);
  };

  const handleDescricaoChange = (descricao: string) => {
    setDescricoesSelecionadas((prev) => {
      if (prev.includes(descricao)) {
        return prev.filter((desc) => desc !== descricao);
      } else {
        return [...prev, descricao];
      }
    });
  };

  const handleSelecionarTodasDescricoes = () => {
    setDescricoesSelecionadas(descricoesUnicas);
  };

  const handleOpenFilterDescricao = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorElDescricao(event.currentTarget);
  };

  const handleCloseFilterDescricao = () => {
    setAnchorElDescricao(null);
  };

  const open = Boolean(anchorEl);
  const openPagamento = Boolean(anchorElPagamento);
  const openDescricao = Boolean(anchorElDescricao);

  return (
    <Paper elevation={3} className={styles.sectionPaper}>
      <Box className={styles.sectionHeader}>
        <Typography variant="h5">{titulo}</Typography>

        <TextField
          label="Filtro..."
          variant="outlined"
          size="small"
          value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />

        <div>
          <IconButton
            disabled={isMonthClosed}
            title="Adicionar Transação"
            color="primary"
            onClick={onAddClick}
          >
            <AddCircleOutlineIcon style={{ color: "#26372D" }} />
          </IconButton>

          {titulo === "Entrada" && onResgateClick && (
            <IconButton
              disabled={isMonthClosed}
              title="Resgate Saldo Acumulado"
              color="primary"
              onClick={onResgateClick}
            >
              <AccountBalanceWalletIcon style={{ color: "#26372D" }} />
            </IconButton>
          )}

          {!isMobile && (
            <IconButton
              title={
                heightTable === "300px" ? "Expandir Tabela" : "Reduzir Tabela"
              }
              color="primary"
              onClick={() =>
                setHeightTable(heightTable === "300px" ? "100%" : "300px")
              }
            >
              {heightTable === "300px" ? (
                <UnfoldMore style={{ color: "#26372D" }} />
              ) : (
                <UnfoldLess style={{ color: "#26372D" }} />
              )}
            </IconButton>
          )}
        </div>
      </Box>

      <TransactionList
        transactions={dadosFiltrados}
        isMonthClosed={isMonthClosed}
        onDelete={onDeleteClick}
        onUpdateCheck={onUpdateTscCheckClick}
        onUpdateValue={onUpdateValueTransactionClick}
        onDeleteConfirm={onDeleteConfirmClick}
        onCheckConfirm={onCheckConfirmClick}
      >
        <TableContainer sx={{ maxHeight: heightTable }}>
        <Table stickyHeader aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                Ações
              </TableCell>
              {titulo === "Saída" && (
                <TableCell sx={{ fontWeight: "bold" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    Pagamento Efetuado
                    <IconButton
                      size="small"
                      title="Filtrar por Status de Pagamento"
                      onClick={handleOpenFilterPagamento}
                      sx={{
                        padding: 0.5,
                        position: "relative",
                      }}
                    >
                      <FilterListIcon
                        sx={{
                          fontSize: 16,
                          color:
                            pagamentosSelecionados.length < 2
                              ? "#1976d2"
                              : "#26372D",
                        }}
                      />
                      {pagamentosSelecionados.length < 2 && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: 8,
                            height: 8,
                            borderRadius: "50%",
                            backgroundColor: "#1976d2",
                          }}
                        />
                      )}
                    </IconButton>
                  </Box>
                </TableCell>
              )}
              <TableCell sx={{ fontWeight: "bold" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Descrição
                  <IconButton
                    size="small"
                    title="Filtrar por Descrição"
                    onClick={handleOpenFilterDescricao}
                    sx={{
                      padding: 0.5,
                      position: "relative",
                    }}
                  >
                    <FilterListIcon
                      sx={{
                        fontSize: 16,
                        color:
                          descricoesSelecionadas.length > 0 &&
                          descricoesSelecionadas.length < descricoesUnicas.length
                            ? "#1976d2"
                            : "#26372D",
                      }}
                    />
                    {descricoesSelecionadas.length > 0 &&
                      descricoesSelecionadas.length < descricoesUnicas.length && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#1976d2",
                        }}
                      />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  Categoria
                  <IconButton
                    size="small"
                    title="Filtrar por Categoria"
                    onClick={handleOpenFilter}
                    sx={{
                      padding: 0.5,
                      position: "relative",
                    }}
                  >
                    <FilterListIcon
                      sx={{
                        fontSize: 16,
                        color:
                          categoriasSelecionadas.length > 0 &&
                          categoriasSelecionadas.length < categoriasUnicas.length
                            ? "#1976d2"
                            : "#26372D",
                      }}
                    />
                    {categoriasSelecionadas.length > 0 &&
                      categoriasSelecionadas.length < categoriasUnicas.length && (
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: "#1976d2",
                        }}
                      />
                    )}
                  </IconButton>
                </Box>
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Recorrência</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Data Final Recorrência
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Usuário da Transação
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }} align="right">
                Valor
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {dadosFiltrados.map((transacao) => (
              <TableRow key={transacao.tscId}>
                <TableCell align="center">
                  <Box>
                    <IconButton
                      disabled={isMonthClosed}
                      title="Remover Transação"
                      color="primary"
                      onClick={(e) => onDeleteConfirmClick(e, transacao.tscId)}
                    >
                      <CancelIcon style={{ color: "#ff00008c" }} />
                    </IconButton>
                  </Box>
                </TableCell>
                {titulo === "Saída" && (
                  <TableCell align="center">
                    <Switch
                      disabled={isMonthClosed}
                      checked={transacao.tscCheck === 1 ? true : false}
                      onChange={(e, checked) => {
                        const newChecked = checked ? 1 : 0;
                        onCheckConfirmClick(e as any, transacao.tscId, newChecked);
                      }}
                      name="check"
                      color="primary"
                    />
                  </TableCell>
                )}
                <TableCell component="th" scope="row">
                  {transacao.tscName}
                </TableCell>
                <TableCell>{transacao.tccName}</TableCell>
                <TableCell>{transacao.tscRecurrence ? "SIM" : "NÃO"}</TableCell>
                <TableCell>
                  {transacao.tscRecurrenceDate
                    ? dayjs(transacao.tscRecurrenceDate).format("DD/MM/YYYY")
                    : ""}
                </TableCell>
                <TableCell>{transacao.usrName}</TableCell>
                <EditableValueCell
                  initialValue={transacao.tscValue}
                  onSave={async (newValue) =>
                    await onUpdateValueTransactionClick(
                      transacao.tscId,
                      newValue
                    )
                  }
                />
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </TableContainer>
      </TransactionList>

      <Box className={styles.totalBox}>
        <Typography variant="body2" color="textSecondary">
          {totalLinhas} {totalLinhas === 1 ? "Registro" : "Registros"}
        </Typography>
        {titulo === "Saída" && (
          <div>
            <Typography variant="body2" color="textSecondary">
              Pagamento Efetuado: {totalPagos}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Pagamento Não Efetuado: {totalNaoPagos}
            </Typography>
          </div>
        )}
        <Typography variant="h6">{totalFormatado}</Typography>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCloseFilter}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Filtrar por Categoria
          </Typography>

          <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelecionarTodas}
            >
              Todas
            </Button>
          </Box>

          <FormGroup>
            {categoriasUnicas.map((categoria) => (
              <FormControlLabel
                key={categoria}
                control={
                  <Checkbox
                    checked={categoriasSelecionadas.includes(categoria)}
                    onChange={() => handleCategoriaChange(categoria)}
                    size="small"
                  />
                }
                label={categoria}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>

      <Popover
        open={openPagamento}
        anchorEl={anchorElPagamento}
        onClose={handleCloseFilterPagamento}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Filtrar por Status de Pagamento
          </Typography>

          <Box sx={{ mb: 2, display: "flex", gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelecionarTodosPagamentos}
            >
              Todos
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={handleDeselecionarTodosPagamentos}
            >
              Nenhum
            </Button>
          </Box>

          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  checked={pagamentosSelecionados.includes(1)}
                  onChange={() => handlePagamentoChange(1)}
                  size="small"
                />
              }
              label="Pago"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={pagamentosSelecionados.includes(0)}
                  onChange={() => handlePagamentoChange(0)}
                  size="small"
                />
              }
              label="Não Pago"
            />
          </FormGroup>
        </Box>
      </Popover>

      <Popover
        open={openDescricao}
        anchorEl={anchorElDescricao}
        onClose={handleCloseFilterDescricao}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Filtrar por Descrição
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={handleSelecionarTodasDescricoes}
            >
              Todas
            </Button>
          </Box>

          <FormGroup>
            {descricoesUnicas.map((descricao) => (
              <FormControlLabel
                key={descricao}
                control={
                  <Checkbox
                    checked={descricoesSelecionadas.includes(descricao)}
                    onChange={() => handleDescricaoChange(descricao)}
                    size="small"
                  />
                }
                label={descricao}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>
    </Paper>
  );
};

const FinancesYear = ({
  onYearView,
}: {
  onYearView: (view: boolean) => void;
}) => {
  // Hook para detectar mobile
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [transactionsYear, setTransactionsYear] = useState<TransactionsYear[]>(
    []
  );
  const [currentDate, setCurrentDate] = useState(dayjs().locale("pt-br"));
  const [loading, setLoading] = useState(true);
  const [userCurrent, setUserCurrent] = useState("");
  const [userTransactions, setUserTransactions] = useState<UserTransaction[]>(
    []
  );
  const { enqueueSnackbar } = useSnackbar();

  const fetchTransactionsYear = useCallback(async () => {
    setLoading(true);
    const year = currentDate.year();

    try {
      const data = await getTransactionsBalance(
        year,
        userCurrent ? userCurrent : undefined
      );
      setTransactionsYear(data);
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || "Falha ao carregar transações.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setTransactionsYear([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, userCurrent, enqueueSnackbar]);

  const fetchUserTransactions = useCallback(async () => {
    try {
      const data = await getUserTransaction();
      setUserTransactions(data);
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || "Falha ao carregar Usuários.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setUserTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const entrada = transactionsYear.reduce(
    (soma, item) => soma + item.totalIn,
    0
  );
  const saida = transactionsYear.reduce(
    (soma, item) => soma + item.totalOut,
    0
  );
  const balanco = transactionsYear.reduce(
    (soma, item) => soma + item.balance,
    0
  );

  const ultimoAcumulado = transactionsYear.at(-1)?.accumulatedBalance || 0;
  const ultimoProjecao =
    transactionsYear.at(-1)?.projectedAccumulatedBalance || 0;

  const entradaFormatado = entrada.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const saidaFormatada = saida.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const balancoFormatado = balanco.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const ultimoAcumuladoFormatado = ultimoAcumulado.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const ultimoProjecaoFormatado = ultimoProjecao.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

  const handlePreviousYear = () => {
    setCurrentDate(currentDate.subtract(1, "year"));
  };

  const handleNextYear = () => {
    setCurrentDate(currentDate.add(1, "year"));
  };

  const resetYear = () => {
    setCurrentDate(dayjs().locale("pt-br"));
  };

  useEffect(() => {
    fetchTransactionsYear();
    fetchUserTransactions();
  }, [fetchTransactionsYear, fetchUserTransactions]);

  return (
    <main className={styles.main}>
      <Box className={styles.monthSelector}>
        <Box className={styles.leftSection}>
          <IconButton title="Ver Mensal" onClick={() => onYearView(false)}>
            <CalendarMonth />
          </IconButton>
        </Box>
        
        <Box className={styles.centerSection}>
          <IconButton onClick={handlePreviousYear}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Box className={styles.yearDisplay}>
            <Typography
              sx={{ cursor: "pointer" }}
              variant="h4"
              component="h1"
              onClick={resetYear}
              title="Voltar para o Ano Atual"
            >
              {currentDate.format("YYYY")}
            </Typography>
            {userCurrent && (
              <Typography variant="subtitle1" component="h2">
                {userTransactions
                  ? userTransactions.find(
                      (u) => u.usrId === Number(userCurrent)
                    )?.usrName
                  : ""}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleNextYear}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>
        
        <Box className={styles.rightSection}>
          {userCurrent && (
            <IconButton
              title="Remover Filtro"
              color="primary"
              onClick={() => setUserCurrent("")}
            >
              <CancelIcon style={{ color: "#ff00008c" }} />
            </IconButton>
          )}
          {userTransactions.map((user) => (
            <UserListAvatar
              key={user.usrId}
              userId={user.usrId}
              userName={user.usrName}
              onClick={(userId) => setUserCurrent(String(userId))}
            />
          ))}
        </Box>
      </Box>

      {loading ? (
        <Box className={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper elevation={3} className={styles.sectionPaper}>
            <Box className={styles.sectionHeader}>
              <Typography variant="h5">Balanço</Typography>
            </Box>

            <YearBalanceList transactions={transactionsYear}>
              <TableContainer>
                <Table stickyHeader aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Ano
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Mês
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Entrada
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Saída
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Saldo do Mês
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Saldo Acumulado
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: "bold" }}>
                        Saldo Projetado
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactionsYear.map((transacao) => (
                      <TableRow
                        key={`${transacao.tscYear}:${transacao.tscMonth}`}
                      >
                        <TableCell component="th" scope="row" align="center">
                          {transacao.tscYear}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          {new Date(0, transacao.tscMonth - 1).toLocaleString(
                            "pt-BR",
                            { month: "long" }
                          )}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          {transacao.totalIn.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          {transacao.totalOut.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          {transacao.balance.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          {transacao.accumulatedBalance.toLocaleString("pt-BR", {
                            style: "currency",
                            currency: "BRL",
                          })}
                        </TableCell>
                        <TableCell component="th" scope="row" align="center">
                          {transacao.projectedAccumulatedBalance.toLocaleString(
                            "pt-BR",
                            {
                              style: "currency",
                              currency: "BRL",
                            }
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </YearBalanceList>

            <Box 
              className={styles.totalBox}
              sx={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: isMobile ? 1 : 2,
                p: isMobile ? 2 : 3,
                backgroundColor: isMobile ? '#f8f9fa' : 'transparent',
                borderRadius: isMobile ? 2 : 0,
                border: isMobile ? '1px solid #e0e0e0' : 'none'
              }}
            >
              <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "h6"} 
                  sx={{ 
                    color: '#4caf50', 
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1.25rem'
                  }}
                >
                  Entrada: {entradaFormatado}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "h6"} 
                  sx={{ 
                    color: '#f44336', 
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1.25rem'
                  }}
                >
                  Saída: {saidaFormatada}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "h6"} 
                  sx={{ 
                    color: 'text.primary', 
                    fontWeight: 700,
                    fontSize: isMobile ? '1rem' : '1.25rem'
                  }}
                >
                  Saldo Anual: {balancoFormatado}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "h6"} 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1.25rem'
                  }}
                >
                  Acumulo Real: {ultimoAcumuladoFormatado}
                </Typography>
              </Box>
              
              <Box sx={{ textAlign: isMobile ? 'center' : 'left' }}>
                <Typography 
                  variant={isMobile ? "body2" : "h6"} 
                  sx={{ 
                    color: 'text.secondary', 
                    fontWeight: 600,
                    fontSize: isMobile ? '0.9rem' : '1.25rem'
                  }}
                >
                  Projeção Acumulada: {ultimoProjecaoFormatado}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </>
      )}
    </main>
  );
};

const FinancesMonth = ({
  onYearView,
}: {
  onYearView: (view: boolean) => void;
}) => {
  // Hook para acessar dados do usuário autenticado
  const { userId, userName, token } = useUser();

  const [transacoes, setTransacoes] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(dayjs().locale("pt-br"));
  const [userCurrent, setUserCurrent] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transactionType, setTransactionType] = useState<"Entrada" | "Saída">(
    "Saída"
  );
  const [userTransactions, setUserTransactions] = useState<UserTransaction[]>(
    []
  );



  const [transactionsYear, setTransactionsYear] = useState<TransactionsYear[]>(
    []
  );
  const [anchorElFecharMes, setAnchorElFecharMes] =
    useState<HTMLButtonElement | null>(null);
  const [anchorElDeleteConfirm, setAnchorElDeleteConfirm] =
    useState<HTMLButtonElement | null>(null);
  const [anchorElCheckConfirm, setAnchorElCheckConfirm] =
    useState<HTMLButtonElement | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [pendingCheckData, setPendingCheckData] = useState<{
    id: number;
    checked: number;
  } | null>(null);
  const [isResgateModalOpen, setIsResgateModalOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  // Comparação de mês (anterior/próximo)
  const [compareMode, setCompareMode] = useState<"prev" | "next" | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);
  const [compareTransacoes, setCompareTransacoes] = useState<Transaction[]>([]);
  const [anchorElCompare, setAnchorElCompare] = useState<null | HTMLElement>(null);
  const compareDate = compareMode
    ? (compareMode === "next" ? currentDate.add(1, "month") : currentDate.subtract(1, "month"))
    : null;

  const openCompareMenu = (e: React.MouseEvent<HTMLElement>) => setAnchorElCompare(e.currentTarget);
  const closeCompareMenu = () => setAnchorElCompare(null);
  const chooseCompare = (mode: "prev" | "next") => {
    setCompareMode(compareMode === mode ? null : mode);
    closeCompareMenu();
  };

  // Evita loops: dispara fetch só quando compareMode muda ou mês/ano/usuário mudam
  useEffect(() => {
    let ignore = false;
    const run = async () => {
      if (!compareDate || !compareMode) {
        setCompareTransacoes([]);
        setCompareLoading(false);
        return;
      }
      setCompareLoading(true);
      try {
        const data = await getTransactionsByDate(
          compareDate.month() + 1,
          compareDate.year(),
          userCurrent ? userCurrent : undefined
        );
        if (!ignore) setCompareTransacoes(data);
      } catch (error: any) {
        const errorMessage = error?.error || error?.message || "Falha ao carregar transações de comparação.";
        enqueueSnackbar(errorMessage, { variant: "error" });
        if (!ignore) setCompareTransacoes([]);
      } finally {
        if (!ignore) setCompareLoading(false);
      }
    };
    run();
    return () => { ignore = true; };
  }, [compareMode, userCurrent, enqueueSnackbar, currentDate]);

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    const month = currentDate.month() + 1;
    const year = currentDate.year();

    try {
      const data = await getTransactionsByDate(
        month,
        year,
        userCurrent ? userCurrent : undefined
      );
      setTransacoes(data);
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || "Falha ao carregar transações.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setTransacoes([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, userCurrent, enqueueSnackbar]);

  const fetchTransactionsYear = useCallback(async () => {
    const year = currentDate.year();

    try {
      const data = await getTransactionsBalance(
        year,
        userCurrent ? userCurrent : undefined
      );
      setTransactionsYear(data);
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || "Falha ao carregar transações.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setTransactionsYear([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, userCurrent, enqueueSnackbar]);

  const fetchUserTransactions = useCallback(async () => {
    try {
      const data = await getUserTransaction();
      setUserTransactions(data);
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || "Falha ao carregar Usuários.";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setUserTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  const deletedTransaction = useCallback(
    async (transactionId: number) => {
      try {
        await deleteTransaction(transactionId);
        setTransacoes((prev) => prev.filter((t) => t.tscId !== transactionId));
        enqueueSnackbar("Transação removida com sucesso.", {
          variant: "success",
        });
      } catch (error: any) {
        const errorMessage = error?.error || error?.message || "Falha ao remover transação.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const updatedValueTransaction = useCallback(
    async (tscId: number, tscValue: number) => {
      try {
        await updateValueTransaction({ tscId, tscValue });

        setTransacoes((prev) =>
          prev.map((t) =>
            t.tscId === tscId ? { ...t, tscValue: tscValue } : t
          )
        );

        enqueueSnackbar("Transação atualizada com sucesso.", {
          variant: "success",
        });
      } catch (error: any) {
        const errorMessage = error?.error || error?.message || "Falha ao atualizar transação.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const updatedTscCheckTransaction = useCallback(
    async (tscId: number, tscCheck: number) => {
      try {
        await updateTscCheckTransaction(tscId, tscCheck);

        setTransacoes((prev) =>
          prev.map((transacao) =>
            transacao.tscId === tscId ? { ...transacao, tscCheck } : transacao
          )
        );

        enqueueSnackbar("Transação atualizada com sucesso.", {
          variant: "success",
        });
      } catch (error: any) {
        const errorMessage = error?.error || error?.message || "Falha ao atualizar transação.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const closeBalanceTransactions = useCallback(
    async (usrId: number, year: number, month: number) => {
      try {
        await closeMonthBalance({ usrId, umbYear: year, umbMonth: month });

        fetchTransactionsYear();

        enqueueSnackbar("Mês fechado com sucesso.", {
          variant: "success",
        });
      } catch (error: any) {
        // Captura a mensagem de erro real da API
        const errorMessage = error?.error || error?.message || "Falha ao fechar mês.";
        enqueueSnackbar(errorMessage, { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  useEffect(() => {
    fetchTransactions();
    fetchUserTransactions();
    fetchTransactionsYear();
  }, [fetchTransactions, fetchUserTransactions, fetchTransactionsYear]);

  const handleOpenModal = (type: "Entrada" | "Saída") => {
    setTransactionType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => setIsModalOpen(false);

  const handleSaveSuccess = (novaTransacao: Transaction) => {
    const month = currentDate.month() + 1;
    const year = currentDate.year();
    if (novaTransacao.tscMonth === month && novaTransacao.tscYear === year) {
      setTransacoes((prev) => [...prev, novaTransacao]);
    }
  };

  const handlePreviousMonth = () => {
    setCurrentDate(currentDate.subtract(1, "month"));
  };

  const handleNextMonth = () => {
    setCurrentDate(currentDate.add(1, "month"));
  };

  const resetMonthAndYear = () => {
    setCurrentDate(dayjs().locale("pt-br"));
  };

  const handleOpenFecharMes = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElFecharMes(event.currentTarget);
  };

  const handleCloseFecharMes = () => {
    setAnchorElFecharMes(null);
  };

  const handleConfirmarFecharMes = () => {
    setAnchorElFecharMes(null);
    closeBalanceTransactions(1, currentDate.year(), currentDate.month() + 1);
  };

  // Funções para confirmação de delete
  const handleOpenDeleteConfirm = (event: React.MouseEvent<HTMLButtonElement>, id: number) => {
    event.stopPropagation();
    setPendingDeleteId(id);
    setAnchorElDeleteConfirm(event.currentTarget);
  };

  const handleCloseDeleteConfirm = () => {
    setAnchorElDeleteConfirm(null);
    setPendingDeleteId(null);
  };

  const handleConfirmarDelete = () => {
    if (pendingDeleteId !== null) {
      deletedTransaction(pendingDeleteId);
    }
    handleCloseDeleteConfirm();
  };

  // Funções para confirmação de mudança de status
  const handleOpenCheckConfirm = (event: React.MouseEvent<HTMLElement>, id: number, checked: number) => {
    event.stopPropagation();
    setPendingCheckData({ id, checked });
    setAnchorElCheckConfirm(event.currentTarget as HTMLButtonElement);
  };

  const handleCloseCheckConfirm = () => {
    setAnchorElCheckConfirm(null);
    setPendingCheckData(null);
  };

  const handleConfirmarCheck = () => {
    if (pendingCheckData !== null) {
      updatedTscCheckTransaction(pendingCheckData.id, pendingCheckData.checked);
    }
    handleCloseCheckConfirm();
  };

  // Funções para o modal de resgate
  const handleOpenResgateModal = () => {
    setIsResgateModalOpen(true);
  };

  const handleCloseResgateModal = () => {
    setIsResgateModalOpen(false);
  };

  const handleResgateSubmit = async (resgateData: any[]) => {
    try {
      const month = currentDate.month() + 1;
      const year = currentDate.year();

      // Aqui você vai implementar a chamada para a API quando ela estiver pronta

      enqueueSnackbar("Resgate realizado com sucesso!", { variant: "success" });
      
      // Recarregar as transações após o resgate
      fetchTransactions();
    } catch (error) {
      enqueueSnackbar("Erro ao realizar resgate", { variant: "error" });
    }
  };

  let saidas: any[] = [];
  let entradas: any[] = [];

  if (Array.isArray(transacoes)) {
    saidas = transacoes.filter((t) => t.tctName === "Saída");
    entradas = transacoes.filter((t) => t.tctName === "Entrada");
  }
  const totalEntradas = entradas.reduce((acc, item) => acc + item.tscValue, 0);
  const totalSaidas = saidas.reduce((acc, item) => acc + item.tscValue, 0);
  const balancoMes = totalEntradas - totalSaidas;

  const acumulo = transactionsYear.filter((t) => {
    return (
      t.tscMonth === currentDate.month() && t.tscYear === currentDate.year()
    );
  });

  const ultimoAcumuladoFormatado =
    acumulo?.[0]?.accumulatedBalance?.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  const ultimoProjecaoAcumuladoFormatado =
    acumulo?.[0]?.projectedAccumulatedBalance?.toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });

  const closeMonthCheck = transactionsYear.filter(
    (b) =>
      b.tscMonth === currentDate.month() + 1 && b.tscYear === currentDate.year()
  )[0];
  const isMonthClosed = closeMonthCheck
    ? closeMonthCheck?.accumulatedBalance === 0
      ? false
      : true
    : userCurrent ? true : false;

  return (
    <main className={styles.main}>
      <Box className={styles.monthSelector}>
        <Box className={styles.leftSection}>
          <IconButton title="Ver Anual" onClick={() => onYearView(true)}>
            <CalendarToday fontSize="medium" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <IconButton
            disabled={isMonthClosed}
            title={isMonthClosed ? "Mês Fechado" : "Fechar Mês"}
            onClick={handleOpenFecharMes}
          >
            <EventAvailable fontSize="medium" />
          </IconButton>
          <Divider orientation="vertical" flexItem />
          <Typography className={styles.saldoAcumulado}>
            Saldo Acumulado{" "}
            {currentDate
              .subtract(1, "month")
              .format("MMMM")
              .charAt(0)
              .toUpperCase() +
              currentDate.subtract(1, "month").format("MMMM").slice(1)}
            : <br />
            {ultimoAcumuladoFormatado === "R$ 0,00"
              ? "R$ 0,00"
              : ultimoProjecaoAcumuladoFormatado || "R$ 0,00"}
          </Typography>
        </Box>
        
        <Box className={styles.centerSection}>
          <IconButton onClick={handlePreviousMonth}>
            <ArrowBackIosNewIcon />
          </IconButton>
          <Box className={styles.yearDisplay}>
            <Typography
              sx={{ cursor: "pointer" }}
              onClick={resetMonthAndYear}
              variant="h4"
              component="h1"
              title="Voltar para o Mês Atual"
            >
              {currentDate.format("MMMM [de] YYYY")}
            </Typography>
            {userCurrent && (
              <Typography variant="subtitle1" component="h2">
                {userTransactions
                  ? userTransactions.find(
                      (u) => u.usrId === Number(userCurrent)
                    )?.usrName
                  : ""}
              </Typography>
            )}

            {/* Botão de comparação compacto */}
            <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center' }}>
              <Button
                size="small"
                variant={compareMode ? 'contained' : 'outlined'}
                onClick={openCompareMenu}
                style={{ borderColor: '#26372D', color: compareMode ? '#fff' : '#26372D', backgroundColor: compareMode ? '#26372D' : undefined }}
              >
                {compareMode ? `Comparando ${compareMode === 'prev' ? 'mês anterior' : 'próximo mês'}` : 'Comparar mês'}
              </Button>
              <Menu
                anchorEl={anchorElCompare}
                open={Boolean(anchorElCompare)}
                onClose={closeCompareMenu}
              >
                <MenuItem onClick={() => chooseCompare('prev')}>{compareMode === 'prev' ? 'Remover comparação anterior' : 'Comparar com mês anterior'}</MenuItem>
                <MenuItem onClick={() => chooseCompare('next')}>{compareMode === 'next' ? 'Remover comparação próxima' : 'Comparar com próximo mês'}</MenuItem>
              </Menu>
            </Box>
          </Box>
          <IconButton onClick={handleNextMonth}>
            <ArrowForwardIosIcon />
          </IconButton>
        </Box>

        <Box className={styles.rightSection}>
          {userCurrent && (
            <IconButton
              title="Remover Filtro"
              color="primary"
              onClick={() => setUserCurrent("")}
            >
              <CancelIcon style={{ color: "#ff00008c" }} />
            </IconButton>
          )}
          {userTransactions.map((user) => (
            <UserListAvatar
              key={user.usrId}
              userId={user.usrId}
              userName={user.usrName}
              onClick={(userId) => setUserCurrent(String(userId))}
            />
          ))}
        </Box>
        {/* </Box> */}
      </Box>

      {loading && (
        <Box className={styles.loadingContainer}>
          <CircularProgress />
        </Box>
      )}

      {/* Grid 2 colunas: atual à esquerda, comparação à direita */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0,1fr)', md: compareMode ? 'minmax(0,1fr) minmax(0,1fr)' : 'minmax(0,1fr)' },
        columnGap: 3,
        rowGap: 3,
        alignItems: 'start',
        '& > *': { minWidth: 0 }
      }}>
        {/* Coluna: Mês atual */}
        <Box>
          <Box sx={{ mb: 2 }}>
            <TableFinance
            titulo="Saída"
            dados={saidas}
            isMonthClosed={isMonthClosed}
            onAddClick={() => handleOpenModal("Saída")}
            onDeleteClick={deletedTransaction}
            onUpdateTscCheckClick={updatedTscCheckTransaction}
            onUpdateValueTransactionClick={updatedValueTransaction}
            onDeleteConfirmClick={handleOpenDeleteConfirm}
            onCheckConfirmClick={handleOpenCheckConfirm}
            resetKey={currentDate.format('YYYY-MM')}
          />
        </Box>
          <Box sx={{ mt: 3 }}>
            <TableFinance
              titulo="Entrada"
              dados={entradas}
              isMonthClosed={isMonthClosed}
              onAddClick={() => handleOpenModal("Entrada")}
              onDeleteClick={deletedTransaction}
              onUpdateTscCheckClick={updatedTscCheckTransaction}
              onUpdateValueTransactionClick={updatedValueTransaction}
              onDeleteConfirmClick={handleOpenDeleteConfirm}
              onCheckConfirmClick={handleOpenCheckConfirm}
              onResgateClick={handleOpenResgateModal}
              resetKey={currentDate.format('YYYY-MM')}
            />
          </Box>
          <Paper elevation={3} className={styles.balanceBar}>
            <Box className={styles.balanceItem}>
              <Typography variant="caption">Entradas</Typography>
              <Typography variant="h6" className={styles.positivo}>
                {totalEntradas.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box className={styles.balanceItem}>
              <Typography variant="caption">Saídas</Typography>
              <Typography variant="h6" className={styles.negativo}>
                {totalSaidas.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box className={styles.balanceItem}>
              <Typography variant="caption">Saldo do Mês</Typography>
              <Typography
                variant="h6"
                className={balancoMes >= 0 ? styles.positivo : styles.negativo}
              >
                {balancoMes.toLocaleString("pt-BR", {
                  style: "currency",
                  currency: "BRL",
                })}
              </Typography>
            </Box>
            {isMonthClosed && (
              <>
                <Divider orientation="vertical" flexItem />
                <Box className={styles.balanceItem}>
                  <Typography variant="caption">Mês Fechado</Typography>
                  <Typography
                    variant="h6"
                    className={
                      balancoMes >= 0 ? styles.positivo : styles.negativo
                    }
                  >
                    {closeMonthCheck?.accumulatedBalance?.toLocaleString("pt-BR", {
                      style: "currency",
                      currency: "BRL",
                    })}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </Box>

        {compareMode && (
          <Box>
            {compareLoading && (
              <Box className={styles.loadingContainer}>
                <CircularProgress />
              </Box>
            )}

            {(() => {
              let cSaidas: any[] = [];
              let cEntradas: any[] = [];
              if (Array.isArray(compareTransacoes)) {
                cSaidas = compareTransacoes.filter((t) => t.tctName === "Saída");
                cEntradas = compareTransacoes.filter((t) => t.tctName === "Entrada");
              }
              const cTotalEntradas = cEntradas.reduce((acc, item) => acc + item.tscValue, 0);
              const cTotalSaidas = cSaidas.reduce((acc, item) => acc + item.tscValue, 0);
              const cBalancoMes = cTotalEntradas - cTotalSaidas;

              return (
                <>
                  <Box sx={{ mb: 2 }}>
                    <TableFinance
                      titulo={`Saída — ${compareDate?.format("MMMM [de] YYYY")}`}
                      dados={cSaidas}
                      isMonthClosed={true}
                      onAddClick={() => {}}
                      onDeleteClick={() => {}}
                      onUpdateTscCheckClick={() => {}}
                      onUpdateValueTransactionClick={() => {}}
                      onDeleteConfirmClick={() => {}}
                      onCheckConfirmClick={() => {}}
                      resetKey={`${compareDate?.format('YYYY-MM')}-compare`}
                    />
                  </Box>
                  <Box sx={{ mt: 3 }}>
                    <TableFinance
                      titulo={`Entrada — ${compareDate?.format("MMMM [de] YYYY")}`}
                      dados={cEntradas}
                      isMonthClosed={true}
                      onAddClick={() => {}}
                      onDeleteClick={() => {}}
                      onUpdateTscCheckClick={() => {}}
                      onUpdateValueTransactionClick={() => {}}
                      onDeleteConfirmClick={() => {}}
                      onCheckConfirmClick={() => {}}
                      resetKey={`${compareDate?.format('YYYY-MM')}-compare`}
                    />
                  </Box>

                  <Paper elevation={3} className={styles.balanceBar}>
                    <Box className={styles.balanceItem}>
                      <Typography variant="caption">Entradas</Typography>
                      <Typography variant="h6" className={styles.positivo}>
                        {cTotalEntradas.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box className={styles.balanceItem}>
                      <Typography variant="caption">Saídas</Typography>
                      <Typography variant="h6" className={styles.negativo}>
                        {cTotalSaidas.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </Typography>
                    </Box>
                    <Divider orientation="vertical" flexItem />
                    <Box className={styles.balanceItem}>
                      <Typography variant="caption">Saldo do Mês</Typography>
                      <Typography
                        variant="h6"
                        className={cBalancoMes >= 0 ? styles.positivo : styles.negativo}
                      >
                        {cBalancoMes.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                      </Typography>
                    </Box>
                  </Paper>
                </>
              );
            })()}
          </Box>
        )}
      </Box>

      <TransactionModal
        open={isModalOpen}
        onClose={handleCloseModal}
        type={transactionType}
        onSaveSuccess={handleSaveSuccess}
      />

      <ResgateModal
        open={isResgateModalOpen}
        onClose={handleCloseResgateModal}
        userTransactions={userTransactions}
        onSubmit={handleResgateSubmit}
      />

      <Popover
        open={Boolean(anchorElFecharMes)}
        anchorEl={anchorElFecharMes}
        onClose={handleCloseFecharMes}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Tem certeza que deseja fechar o mês?
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={handleCloseFecharMes}
              style={{ color: "#26372D" }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              onClick={handleConfirmarFecharMes}
              style={{ backgroundColor: "#26372D", color: "#fff" }}
            >
              Sim, fechar
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Modal de confirmação para deletar transação */}
      <Popover
        open={Boolean(anchorElDeleteConfirm)}
        anchorEl={anchorElDeleteConfirm}
        onClose={handleCloseDeleteConfirm}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Tem certeza que deseja remover esta transação?
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={handleCloseDeleteConfirm}
              style={{ color: "#26372D" }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              onClick={handleConfirmarDelete}
              style={{ backgroundColor: "#26372D", color: "#fff" }}
            >
              Sim, remover
            </Button>
          </Box>
        </Box>
      </Popover>

      {/* Modal de confirmação para alterar status de pagamento */}
      <Popover
        open={Boolean(anchorElCheckConfirm)}
        anchorEl={anchorElCheckConfirm}
        onClose={handleCloseCheckConfirm}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 250 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Tem certeza que deseja alterar o status de pagamento?
          </Typography>
          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button
              size="small"
              onClick={handleCloseCheckConfirm}
              style={{ color: "#26372D" }}
            >
              Cancelar
            </Button>
            <Button
              size="small"
              onClick={handleConfirmarCheck}
              style={{ backgroundColor: "#26372D", color: "#fff" }}
            >
              Sim, alterar
            </Button>
          </Box>
        </Box>
      </Popover>
    </main>
  );
};

export function Finances() {
  const [isYearView, setIsYearView] = useState(false);
  return isYearView ? (
    <FinancesYear onYearView={setIsYearView} />
  ) : (
    <FinancesMonth onYearView={setIsYearView} />
  );
}
